import clsx from 'clsx';
import React from 'react';
import toast from 'react-hot-toast';

import { SecondaryButton } from '@components/Button/variants';
import IconLockWithCircle from '@components/Images/Rebranding/IconLockWithCircle';
import IconPlusWithCircle from '@components/Images/Rebranding/IconPlusWithCircle';
import useSubscribeToNewsletter from '@hooks/useSubscribeToNewsletter';
import useUnsubscribeFromNewsletter from '@hooks/useUnsubscribeFromNewsletter';
import { ClickTarget } from '@tracking';
import { handleSubscriberRedirect } from '@utils/newsletterHelper';

import {
  NewsletterActionButtonProps,
  RenderButtonProps,
  SIGN_UP,
  SUBSCRIBERS_ONLY,
  UNSUBSCRIBE,
} from './interfaces';

const NewsletterActionButton = ({
  afterSignUpUrl,
  canSubscribeToNewsletter,
  currentUserSubscribed,
  id,
  initialCurrentUser,
  isCurrentlySubscriber,
  isNewsletterLanding,
  isPro,
  isProSubscriber,
  newsletterName,
  offers,
  onClickCapture,
  toggleModal,
  redirectToAfterSubscribe,
  updateStateHandlerOnSubscribe,
  updateStateHandlerOnUnsubscribe,
}: NewsletterActionButtonProps) => {
  const { mutateAsync: subscribeToNewsletter, isError: isErrorSubscribing } =
    useSubscribeToNewsletter();
  const { mutateAsync: unsubscribeFromNewsletter, isError: isErrorUnsubscribing } =
    useUnsubscribeFromNewsletter();

  const handleSubscribe = async () => {
    await subscribeToNewsletter({ newsletter_id: id });
    if (isErrorSubscribing) {
      toast.error('Failed to subscribe. Please try again later.');
      return;
    }
    updateStateHandlerOnSubscribe();
  };

  const handleUnsubscribe = async () => {
    await unsubscribeFromNewsletter({ newsletter_id: id });
    if (isErrorUnsubscribing) {
      toast.error('Failed to unsubscribe. Please try again later.');
      return;
    }
    updateStateHandlerOnUnsubscribe();
  };

  const RenderButton = ({
    as,
    clickTarget,
    customClassName,
    href,
    onClickCapture,
    onClick,
    icon,
    label,
    textColor = 'text-grayscale-gray',
  }: RenderButtonProps) => {
    return (
      <SecondaryButton
        as={as}
        {...(as === 'link' ? { href } : { onClick })}
        onClickCapture={() => onClickCapture(clickTarget, label)}
        className={clsx(
          `group rounded-[100px] !border-grayscale-gray bg-white py-6 pl-6 pr-16 hover:!bg-primary-magenta focus:bg-white enabled:hover:!bg-primary-magenta`,
          customClassName,
          {
            'focus:text-grayscale-gray': label === SUBSCRIBERS_ONLY || label === UNSUBSCRIBE,
            'focus:text-primary-magenta': label === SIGN_UP,
          }
        )}
      >
        <div className={`flex gap-8 group-hover:text-white ${textColor}`}>
          {icon}
          <div className={`font-suisse-intl text-button-sm ${textColor} group-hover:text-white`}>
            {label}
          </div>
        </div>
      </SecondaryButton>
    );
  };

  const getActionConfig = () => {
    if (!canSubscribeToNewsletter) {
      return RenderButton({
        as: 'link',
        customClassName: 'max-w-[160px]',
        href: handleSubscriberRedirect({
          afterSignUpUrl: afterSignUpUrl,
          canSubscribeToNewsletter: canSubscribeToNewsletter,
          isCurrentlySubscriber: isCurrentlySubscriber,
          isPro: isPro,
          isProSubscriber: isProSubscriber,
          offers: offers,
          redirectTo: redirectToAfterSubscribe,
        }),
        icon: (
          <IconLockWithCircle className="fill-grayscale-gray stroke-white text-grayscale-gray group-hover:stroke-primary-magenta group-hover:!text-white" />
        ),
        label: SUBSCRIBERS_ONLY,
        onClickCapture: onClickCapture,
        clickTarget: ClickTarget.SubscribeNewsletter,
      });
    }

    if (currentUserSubscribed) {
      return RenderButton({
        as: 'button',
        customClassName: 'max-w-[140px]',
        onClick: handleUnsubscribe,
        onClickCapture: onClickCapture,
        icon: (
          <IconPlusWithCircle className="rotate-45 fill-grayscale-gray stroke-white text-grayscale-gray group-hover:stroke-primary-magenta group-hover:!text-white " />
        ),
        label: UNSUBSCRIBE,
        clickTarget: ClickTarget.UnsubscribeNewsletter,
      });
    }

    return RenderButton({
      as: 'button',
      customClassName: isNewsletterLanding
        ? 'inline-flex max-w-fit min-w-[100px]'
        : 'max-w-[100px]',
      onClick: initialCurrentUser ? handleSubscribe : toggleModal,
      onClickCapture: onClickCapture,
      icon: (
        <IconPlusWithCircle className="fill-primary-magenta stroke-white group-hover:fill-white group-hover:stroke-primary-magenta" />
      ),
      label: isNewsletterLanding ? `Subscribe to ${newsletterName}` : SIGN_UP,
      clickTarget: ClickTarget.SubscribeNewsletter,
      textColor: '',
    });
  };

  return getActionConfig();
};

export default NewsletterActionButton;
